import { buildCollection } from '@firecms/core';

type UserProfile = {
  uid: string;
  email: string;
  fullName: string | null;
  displayName?: string | null;
  dateOfBirth: string | null;
  gender: ProfileGender | null;
  location: string | null;
  onboarding: OnboardingState | null;
  bio: string | null;
  photoURL: string | null;
  coverPhotoURL: string | null;
  profileImages?: ProfileImages | null;
  notificationSettings?: NotificationSettings | null;
  legal?: {
    termsVersion?: string;
    privacyVersion?: string;
    termsDocumentId?: string;
    privacyDocumentId?: string;
    termsContentHash?: string;
    privacyContentHash?: string;
    acceptedAt?: Date;
  };
  createdAt: Date;
  updatedAt: Date;
};

type PublicProfile = {
  uid: string;
  fullName: string | null;
  displayName?: string | null;
  gender: ProfileGender | null;
  location: string | null;
  bio: string | null;
  photoURL: string | null;
  coverPhotoURL: string | null;
  profileImages?: ProfileImages | null;
  followerCount: number;
  followingCount: number;
  createdAt: Date;
  updatedAt: Date;
};

type ProfileGenderValue = 'female' | 'male';

type ProfileGender = {
  value: ProfileGenderValue;
  customText: string | null;
};

type NotificationPreferenceChannel = {
  commentReactions: boolean;
  likes: boolean;
  comments: boolean;
  follows: boolean;
  platformUpdates: boolean;
};

type NotificationSettings = {
  email: NotificationPreferenceChannel;
  inApp: NotificationPreferenceChannel;
  push: NotificationPreferenceChannel;
  likeEmailDigestCadence: 'hourly';
  platformUpdateDigestCadence: 'daily';
};

type OnboardingState = {
  completedAt: Date;
  ageVerifiedAt: Date;
  minimumAge: 13;
  source: 'email' | 'facebook' | 'profile_update';
};

type LegalAcceptanceRecord = {
  uid: string;
  termsVersion: string;
  privacyVersion: string;
  termsDocumentId: string;
  privacyDocumentId: string;
  termsContentHash: string;
  privacyContentHash: string;
  acceptedAt: Date;
  source: 'registration' | 'update';
};

type LegalDocumentRecord = {
  id: string;
  kind: 'terms' | 'privacy';
  title: string;
  version: string;
  effectiveDate: string;
  contentHash: string;
  status: 'current';
  sections: Array<{
    title: string;
    body: string[];
  }>;
  seededAt?: Date;
  updatedAt?: Date;
};

type ChangeLogEntry = {
  title: string;
  summary: string;
  details: string;
  category:
    | 'functionality'
    | 'algorithm'
    | 'privacy'
    | 'safety'
    | 'notifications'
    | 'email'
    | 'bugfix'
    | 'other';
  impact: 'major' | 'minor';
  status: 'draft' | 'published';
  visibility: 'public' | 'internal';
  effectiveAt: Date;
  publishedAt: Date | null;
  commitSha: string | null;
  releaseId: string | null;
  deployTarget: string | null;
  authorName: string | null;
  createdAt: Date;
  updatedAt: Date;
};

type MediaThumbnail = {
  storagePath: string;
  downloadURL: string;
  contentType: 'image/jpeg';
  sizeBytes: number;
  width: number;
  height: number;
  generatedAt: Date;
};

type ProfileImageAsset = {
  storagePath: string;
  downloadURL: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
};

type ProfileImageOriginal = ProfileImageAsset & {
  uploadedAt: Date;
};

type ProfileImageVariant = Omit<ProfileImageAsset, 'contentType'> & {
  contentType: 'image/jpeg';
  generatedAt: Date;
};

type ProfileImageSet = {
  original: ProfileImageOriginal | null;
  variants: {
    thumb?: ProfileImageVariant;
    display?: ProfileImageVariant;
    card?: ProfileImageVariant;
    hero?: ProfileImageVariant;
  } | null;
  status: 'queued' | 'processing' | 'ready' | 'failed';
  error?: string | null;
  updatedAt: Date;
};

type ProfileImages = {
  avatar: ProfileImageSet | null;
  cover: ProfileImageSet | null;
};

type PostMedia = {
  kind: 'image' | 'video';
  storagePath: string;
  downloadURL: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
  durationMillis: number | null;
  thumbnail: MediaThumbnail | null;
  thumbnailStatus: 'queued' | 'processing' | 'ready' | 'failed' | null;
  thumbnailError?: string | null;
};

type UploadedCommentMedia = {
  kind: 'image' | 'video';
  source: 'upload';
  storagePath: string;
  downloadURL: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
  durationMillis: number | null;
  thumbnail: MediaThumbnail | null;
  thumbnailStatus: 'queued' | 'processing' | 'ready' | 'failed' | null;
  thumbnailError?: string | null;
};

type GiphyCommentMedia = {
  kind: 'gif';
  source: 'giphy';
  giphyId: string;
  title: string;
  pageURL: string;
  previewURL: string;
  originalURL: string;
  width: number;
  height: number;
};

type CommentMedia = UploadedCommentMedia | GiphyCommentMedia;
type Reaction = 'like' | 'love' | 'laugh' | 'wow' | 'fire';
type CommentReaction = Reaction;
type PostReaction = Reaction;

type ReactionCounts = Record<Reaction, number>;
type CommentReactionCounts = ReactionCounts;
type PostReactionCounts = ReactionCounts;

type Post = {
  authorUid: string;
  authorFullName: string | null;
  authorDisplayName?: string | null;
  body: string | null;
  visibility: 'public' | 'friends' | 'just_me';
  media: PostMedia | null;
  likeCount: number;
  reactionCounts: PostReactionCounts;
  reactionCount: number;
  commentCount: number;
  shareCount: number;
  commentMutation: {
    commentId?: string;
    actorUid?: string;
    operation?: 'create' | 'delete';
    updatedAt?: Date;
  } | null;
  createdAt: Date;
  updatedAt: Date;
};

type PostComment = {
  authorUid: string;
  authorFullName: string | null;
  authorDisplayName?: string | null;
  authorPhotoURL: string | null;
  body: string | null;
  media: CommentMedia | null;
  reactionCounts: CommentReactionCounts;
  reactionCount: number;
  createdAt: Date;
  updatedAt: Date;
};

type CommentReactionRecord = {
  uid: string;
  postId: string;
  commentId: string;
  reaction: CommentReaction;
  createdAt: Date;
  updatedAt: Date;
};

type PostReactionRecord = {
  uid: string;
  postId: string;
  reaction: PostReaction;
  createdAt: Date;
  updatedAt: Date;
};

type AppNotification = {
  recipientUid: string;
  actorUid: string;
  actorFullName: string | null;
  actorDisplayName?: string | null;
  actorPhotoURL: string | null;
  type:
    | 'post_like'
    | 'post_reaction'
    | 'post_comment'
    | 'profile_follow'
    | 'comment_reaction'
    | 'platform_update_digest';
  postId: string | null;
  commentId: string | null;
  reason?: 'post_owner' | 'comment_thread' | null;
  postPreview: string | null;
  commentPreview: string | null;
  createdAt: Date;
  readAt: Date | null;
};

type FollowRecord = {
  followerUid: string;
  targetUid: string;
  createdAt: Date;
};

type EmailNotificationEvent = {
  recipientUid: string;
  actorUid: string;
  actorFullName: string | null;
  actorPhotoURL: string | null;
  type:
    | 'post_like'
    | 'post_reaction'
    | 'post_comment'
    | 'profile_follow'
    | 'comment_reaction'
    | 'platform_update_digest';
  delivery: 'immediate' | 'digest';
  status: 'queued' | 'sending' | 'sent' | 'failed' | 'skipped';
  postId: string | null;
  commentId: string | null;
  reason?: 'post_owner' | 'comment_thread' | null;
  postPreview: string | null;
  commentPreview: string | null;
  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
  failedAt?: Date;
  skippedAt?: Date;
  error?: string;
};

type PushNotificationEvent = Omit<EmailNotificationEvent, 'delivery'> & {
  failureCount?: number;
  notificationId: string;
  sendingAt?: Date;
  successCount?: number;
  tokenCount?: number;
};

type Organization = {
  name: string;
  ownerUid: string;
  createdAt: Date;
  updatedAt: Date;
};

type OrganizationMember = {
  uid: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: Date;
};

type OrganizationItem = {
  title: string;
  description: string | null;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
};

const adminOnlyPermissions = () => ({
  read: true,
  edit: true,
  create: true,
  delete: true,
});

const adminManagedDates = {
  createdAt: {
    dataType: 'date' as const,
    name: 'Created at',
    autoValue: 'on_create' as const,
    disabled: true,
  },
  updatedAt: {
    dataType: 'date' as const,
    name: 'Updated at',
    autoValue: 'on_update' as const,
    disabled: true,
  },
};

const genderProperties = {
  dataType: 'map' as const,
  name: 'Gender',
  properties: {
    value: {
      dataType: 'string' as const,
      name: 'Value',
      enumValues: {
        female: 'Female',
        male: 'Male',
      },
    },
    customText: {
      dataType: 'string' as const,
      name: 'Custom text',
      clearable: true,
      validation: { max: 60, trim: true },
    },
  },
};

const onboardingProperties = {
  dataType: 'map' as const,
  name: 'Onboarding',
  disabled: true,
  properties: {
    completedAt: {
      dataType: 'date' as const,
      name: 'Completed at',
    },
    ageVerifiedAt: {
      dataType: 'date' as const,
      name: 'Age verified at',
    },
    minimumAge: {
      dataType: 'number' as const,
      name: 'Minimum age',
    },
    source: {
      dataType: 'string' as const,
      name: 'Source',
      enumValues: {
        email: 'Email',
        facebook: 'Facebook',
        profile_update: 'Profile update',
      },
    },
  },
};

const thumbnailProperties = {
  thumbnailStatus: {
    dataType: 'string' as const,
    name: 'Thumbnail status',
    enumValues: {
      queued: 'Queued',
      processing: 'Processing',
      ready: 'Ready',
      failed: 'Failed',
    },
  },
  thumbnailError: {
    dataType: 'string' as const,
    name: 'Thumbnail error',
    multiline: true,
  },
  thumbnail: {
    dataType: 'map' as const,
    name: 'Thumbnail',
    properties: {
      storagePath: {
        dataType: 'string' as const,
        name: 'Storage path',
      },
      downloadURL: {
        dataType: 'string' as const,
        name: 'Download URL',
        url: true,
      },
      contentType: {
        dataType: 'string' as const,
        name: 'Content type',
      },
      sizeBytes: {
        dataType: 'number' as const,
        name: 'Size bytes',
      },
      width: {
        dataType: 'number' as const,
        name: 'Width',
      },
      height: {
        dataType: 'number' as const,
        name: 'Height',
      },
      generatedAt: {
        dataType: 'date' as const,
        name: 'Generated at',
      },
    },
  },
};

const profileImageAssetProperties = {
  storagePath: {
    dataType: 'string' as const,
    name: 'Storage path',
  },
  downloadURL: {
    dataType: 'string' as const,
    name: 'Download URL',
    url: true,
  },
  contentType: {
    dataType: 'string' as const,
    name: 'Content type',
  },
  sizeBytes: {
    dataType: 'number' as const,
    name: 'Size bytes',
  },
  width: {
    dataType: 'number' as const,
    name: 'Width',
  },
  height: {
    dataType: 'number' as const,
    name: 'Height',
  },
};

const profileImageSetProperties = {
  dataType: 'map' as const,
  name: 'Profile image',
  properties: {
    status: {
      dataType: 'string' as const,
      name: 'Status',
      enumValues: {
        queued: 'Queued',
        processing: 'Processing',
        ready: 'Ready',
        failed: 'Failed',
      },
    },
    error: {
      dataType: 'string' as const,
      name: 'Error',
      multiline: true,
      clearable: true,
    },
    original: {
      dataType: 'map' as const,
      name: 'Original',
      properties: {
        ...profileImageAssetProperties,
        uploadedAt: {
          dataType: 'date' as const,
          name: 'Uploaded at',
        },
      },
    },
    variants: {
      dataType: 'map' as const,
      name: 'Generated variants',
      properties: {
        thumb: {
          dataType: 'map' as const,
          name: 'Avatar thumb',
          properties: {
            ...profileImageAssetProperties,
            generatedAt: {
              dataType: 'date' as const,
              name: 'Generated at',
            },
          },
        },
        display: {
          dataType: 'map' as const,
          name: 'Avatar display',
          properties: {
            ...profileImageAssetProperties,
            generatedAt: {
              dataType: 'date' as const,
              name: 'Generated at',
            },
          },
        },
        card: {
          dataType: 'map' as const,
          name: 'Cover card',
          properties: {
            ...profileImageAssetProperties,
            generatedAt: {
              dataType: 'date' as const,
              name: 'Generated at',
            },
          },
        },
        hero: {
          dataType: 'map' as const,
          name: 'Cover hero',
          properties: {
            ...profileImageAssetProperties,
            generatedAt: {
              dataType: 'date' as const,
              name: 'Generated at',
            },
          },
        },
      },
    },
    updatedAt: {
      dataType: 'date' as const,
      name: 'Updated at',
    },
  },
};

const profileImagesProperties = {
  dataType: 'map' as const,
  name: 'Optimized profile images',
  properties: {
    avatar: {
      ...profileImageSetProperties,
      name: 'Avatar',
    },
    cover: {
      ...profileImageSetProperties,
      name: 'Cover',
    },
  },
};

const notificationPreferenceChannelProperties = {
  dataType: 'map' as const,
  properties: {
    commentReactions: {
      dataType: 'boolean' as const,
      name: 'Comment reactions',
    },
    likes: {
      dataType: 'boolean' as const,
      name: 'Post reactions',
    },
    comments: {
      dataType: 'boolean' as const,
      name: 'Comments',
    },
    follows: {
      dataType: 'boolean' as const,
      name: 'Follows',
    },
    platformUpdates: {
      dataType: 'boolean' as const,
      name: 'Daily Frienzzi updates',
    },
  },
};

const commentReactionCountsProperties = {
  dataType: 'map' as const,
  name: 'Reaction counts',
  disabled: true,
  properties: {
    like: {
      dataType: 'number' as const,
      name: 'Like',
    },
    love: {
      dataType: 'number' as const,
      name: 'Love',
    },
    laugh: {
      dataType: 'number' as const,
      name: 'Laugh',
    },
    wow: {
      dataType: 'number' as const,
      name: 'Wow',
    },
    fire: {
      dataType: 'number' as const,
      name: 'Fire',
    },
  },
};

const notificationSettingsProperties = {
  dataType: 'map' as const,
  name: 'Notification settings',
  properties: {
    email: {
      ...notificationPreferenceChannelProperties,
      name: 'Email',
    },
    inApp: {
      ...notificationPreferenceChannelProperties,
      name: 'In-app',
    },
    push: {
      ...notificationPreferenceChannelProperties,
      name: 'Push',
    },
    likeEmailDigestCadence: {
      dataType: 'string' as const,
      name: 'Post reaction email digest cadence',
      enumValues: {
        hourly: 'Hourly',
      },
    },
    platformUpdateDigestCadence: {
      dataType: 'string' as const,
      name: 'Platform update digest cadence',
      enumValues: {
        daily: 'Daily',
      },
    },
  },
};

const membersCollection = buildCollection<OrganizationMember>({
  id: 'organization_members',
  path: 'members',
  name: 'Members',
  singularName: 'Member',
  description:
    'Organization memberships. Use a document ID that matches the member UID.',
  customId: true,
  group: 'Organizations',
  permissions: adminOnlyPermissions,
  properties: {
    uid: {
      dataType: 'string',
      name: 'UID',
      userSelect: true,
      validation: { required: true },
    },
    role: {
      dataType: 'string',
      name: 'Role',
      enumValues: {
        owner: 'Owner',
        admin: 'Admin',
        member: 'Member',
      },
      validation: { required: true },
    },
    joinedAt: {
      dataType: 'date',
      name: 'Joined at',
      autoValue: 'on_create',
      disabled: true,
    },
  },
  propertiesOrder: ['uid', 'role', 'joinedAt'],
});

const itemsCollection = buildCollection<OrganizationItem>({
  id: 'organization_items',
  path: 'items',
  name: 'Items',
  singularName: 'Item',
  customId: false,
  group: 'Organizations',
  permissions: adminOnlyPermissions,
  properties: {
    title: {
      dataType: 'string',
      name: 'Title',
      validation: {
        required: true,
        max: 160,
        trim: true,
      },
    },
    description: {
      dataType: 'string',
      name: 'Description',
      multiline: true,
      clearable: true,
      validation: {
        max: 2000,
      },
    },
    createdBy: {
      dataType: 'string',
      name: 'Created by',
      userSelect: true,
      validation: { required: true },
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'title',
    'description',
    'createdBy',
    'createdAt',
    'updatedAt',
  ],
});

const legalAcceptancesCollection = buildCollection<LegalAcceptanceRecord>({
  id: 'user_legal_acceptances',
  path: 'legalAcceptances',
  name: 'Legal acceptances',
  singularName: 'Legal acceptance',
  description:
    'Immutable timestamped records of Terms and Privacy Policy acceptance.',
  customId: false,
  group: 'Users',
  permissions: () => ({
    read: true,
    edit: false,
    create: false,
    delete: false,
  }),
  properties: {
    uid: {
      dataType: 'string',
      name: 'UID',
      disabled: true,
      validation: { required: true },
    },
    termsVersion: {
      dataType: 'string',
      name: 'Terms version',
      disabled: true,
      validation: { required: true },
    },
    privacyVersion: {
      dataType: 'string',
      name: 'Privacy version',
      disabled: true,
      validation: { required: true },
    },
    termsDocumentId: {
      dataType: 'string',
      name: 'Terms document ID',
      disabled: true,
      validation: { required: true },
    },
    privacyDocumentId: {
      dataType: 'string',
      name: 'Privacy document ID',
      disabled: true,
      validation: { required: true },
    },
    termsContentHash: {
      dataType: 'string',
      name: 'Terms content hash',
      disabled: true,
      validation: { required: true },
    },
    privacyContentHash: {
      dataType: 'string',
      name: 'Privacy content hash',
      disabled: true,
      validation: { required: true },
    },
    acceptedAt: {
      dataType: 'date',
      name: 'Accepted at',
      disabled: true,
    },
    source: {
      dataType: 'string',
      name: 'Source',
      enumValues: {
        registration: 'Registration',
        update: 'Update',
      },
      disabled: true,
    },
  },
  propertiesOrder: [
    'uid',
    'termsVersion',
    'privacyVersion',
    'termsDocumentId',
    'privacyDocumentId',
    'termsContentHash',
    'privacyContentHash',
    'acceptedAt',
    'source',
  ],
});

const notificationsCollection = buildCollection<AppNotification>({
  id: 'user_notifications',
  path: 'notifications',
  name: 'Notifications',
  singularName: 'Notification',
  description:
    'In-app notifications for post reactions, comments, comment reactions, and follows.',
  customId: true,
  group: 'Users',
  permissions: adminOnlyPermissions,
  properties: {
    recipientUid: {
      dataType: 'string',
      name: 'Recipient UID',
      validation: { required: true },
    },
    actorUid: {
      dataType: 'string',
      name: 'Actor UID',
      validation: { required: true },
    },
    actorFullName: {
      dataType: 'string',
      name: 'Actor full name',
      clearable: true,
      validation: { max: 80 },
    },
    actorDisplayName: {
      dataType: 'string',
      name: 'Legacy actor display name',
      clearable: true,
      validation: { max: 80 },
      disabled: true,
    },
    actorPhotoURL: {
      dataType: 'string',
      name: 'Actor photo URL',
      url: 'image',
      clearable: true,
      validation: { max: 2048 },
    },
    type: {
      dataType: 'string',
      name: 'Type',
      enumValues: {
        post_like: 'Post like',
        post_reaction: 'Post reaction',
        post_comment: 'Post comment',
        profile_follow: 'Profile follow',
        comment_reaction: 'Comment reaction',
      },
      validation: { required: true },
    },
    postId: {
      dataType: 'string',
      name: 'Post ID',
      clearable: true,
    },
    commentId: {
      dataType: 'string',
      name: 'Comment ID',
      clearable: true,
    },
    reason: {
      dataType: 'string',
      name: 'Reason',
      enumValues: {
        post_owner: 'Post owner',
        comment_thread: 'Comment thread',
      },
      clearable: true,
    },
    postPreview: {
      dataType: 'string',
      name: 'Post preview',
      clearable: true,
      validation: { max: 140 },
    },
    commentPreview: {
      dataType: 'string',
      name: 'Comment preview',
      clearable: true,
      validation: { max: 140 },
    },
    createdAt: {
      dataType: 'date',
      name: 'Created at',
      disabled: true,
    },
    readAt: {
      dataType: 'date',
      name: 'Read at',
      clearable: true,
    },
  },
  propertiesOrder: [
    'recipientUid',
    'actorUid',
    'actorFullName',
    'actorDisplayName',
    'actorPhotoURL',
    'type',
    'postId',
    'commentId',
    'reason',
    'postPreview',
    'commentPreview',
    'createdAt',
    'readAt',
  ],
});

const followersCollection = buildCollection<FollowRecord>({
  id: 'user_followers',
  path: 'followers',
  name: 'Followers',
  singularName: 'Follower',
  description: 'Users following this profile.',
  customId: true,
  group: 'Users',
  permissions: adminOnlyPermissions,
  properties: {
    followerUid: {
      dataType: 'string',
      name: 'Follower UID',
      userSelect: true,
      validation: { required: true },
    },
    targetUid: {
      dataType: 'string',
      name: 'Target UID',
      userSelect: true,
      validation: { required: true },
    },
    createdAt: {
      dataType: 'date',
      name: 'Created at',
      disabled: true,
    },
  },
  propertiesOrder: ['followerUid', 'targetUid', 'createdAt'],
});

const followingCollection = buildCollection<FollowRecord>({
  id: 'user_following',
  path: 'following',
  name: 'Following',
  singularName: 'Following record',
  description: 'Profiles this user follows.',
  customId: true,
  group: 'Users',
  permissions: adminOnlyPermissions,
  properties: {
    followerUid: {
      dataType: 'string',
      name: 'Follower UID',
      userSelect: true,
      validation: { required: true },
    },
    targetUid: {
      dataType: 'string',
      name: 'Target UID',
      userSelect: true,
      validation: { required: true },
    },
    createdAt: {
      dataType: 'date',
      name: 'Created at',
      disabled: true,
    },
  },
  propertiesOrder: ['followerUid', 'targetUid', 'createdAt'],
});

export const usersCollection = buildCollection<UserProfile>({
  id: 'users',
  path: 'users',
  name: 'Users',
  singularName: 'User',
  description:
    'Firebase Auth-backed profile documents. Auth account creation and deletion are not part of v1 admin.',
  customId: true,
  group: 'Core',
  subcollections: [
    legalAcceptancesCollection,
    notificationsCollection,
    followersCollection,
    followingCollection,
  ],
  permissions: () => ({
    read: true,
    edit: true,
    create: false,
    delete: false,
  }),
  properties: {
    uid: {
      dataType: 'string',
      name: 'UID',
      disabled: true,
      validation: { required: true },
    },
    email: {
      dataType: 'string',
      name: 'Email',
      email: true,
      disabled: true,
      validation: { required: true },
    },
    fullName: {
      dataType: 'string',
      name: 'Full name',
      validation: {
        max: 80,
        trim: true,
      },
    },
    displayName: {
      dataType: 'string',
      name: 'Legacy display name',
      clearable: true,
      disabled: true,
      validation: {
        max: 80,
        trim: true,
      },
    },
    dateOfBirth: {
      dataType: 'string',
      name: 'Date of birth',
      disabled: true,
      validation: {
        max: 10,
        trim: true,
      },
    },
    gender: genderProperties,
    location: {
      dataType: 'string',
      name: 'Location',
      clearable: true,
      validation: {
        max: 120,
        trim: true,
      },
    },
    onboarding: onboardingProperties,
    bio: {
      dataType: 'string',
      name: 'Bio',
      multiline: true,
      clearable: true,
      validation: {
        max: 300,
        trim: true,
      },
    },
    photoURL: {
      dataType: 'string',
      name: 'Photo URL',
      url: 'image',
      clearable: true,
      validation: {
        max: 2048,
      },
    },
    coverPhotoURL: {
      dataType: 'string',
      name: 'Cover photo URL',
      url: 'image',
      clearable: true,
      validation: {
        max: 2048,
      },
    },
    profileImages: profileImagesProperties,
    notificationSettings: notificationSettingsProperties,
    legal: {
      dataType: 'map',
      name: 'Latest legal acceptance',
      disabled: true,
      properties: {
        termsVersion: {
          dataType: 'string',
          name: 'Terms version',
        },
        privacyVersion: {
          dataType: 'string',
          name: 'Privacy version',
        },
        termsDocumentId: {
          dataType: 'string',
          name: 'Terms document ID',
        },
        privacyDocumentId: {
          dataType: 'string',
          name: 'Privacy document ID',
        },
        termsContentHash: {
          dataType: 'string',
          name: 'Terms content hash',
        },
        privacyContentHash: {
          dataType: 'string',
          name: 'Privacy content hash',
        },
        acceptedAt: {
          dataType: 'date',
          name: 'Accepted at',
        },
      },
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'uid',
    'email',
    'fullName',
    'displayName',
    'dateOfBirth',
    'gender',
    'location',
    'onboarding',
    'bio',
    'photoURL',
    'coverPhotoURL',
    'profileImages',
    'notificationSettings',
    'legal',
    'createdAt',
    'updatedAt',
  ],
});

export const publicProfilesCollection = buildCollection<PublicProfile>({
  id: 'public_profiles',
  path: 'publicProfiles',
  name: 'Public profiles',
  singularName: 'Public profile',
  description:
    'Signed-in-readable profile mirror used by feeds and public profile pages.',
  customId: true,
  group: 'Core',
  permissions: adminOnlyPermissions,
  properties: {
    uid: {
      dataType: 'string',
      name: 'UID',
      disabled: true,
      validation: { required: true },
    },
    fullName: {
      dataType: 'string',
      name: 'Full name',
      clearable: true,
      validation: { max: 80, trim: true },
    },
    displayName: {
      dataType: 'string',
      name: 'Legacy display name',
      clearable: true,
      disabled: true,
      validation: { max: 80, trim: true },
    },
    gender: genderProperties,
    location: {
      dataType: 'string',
      name: 'Location',
      clearable: true,
      validation: { max: 120, trim: true },
    },
    bio: {
      dataType: 'string',
      name: 'Bio',
      multiline: true,
      clearable: true,
      validation: { max: 300, trim: true },
    },
    photoURL: {
      dataType: 'string',
      name: 'Photo URL',
      url: 'image',
      clearable: true,
      validation: { max: 2048 },
    },
    coverPhotoURL: {
      dataType: 'string',
      name: 'Cover photo URL',
      url: 'image',
      clearable: true,
      validation: { max: 2048 },
    },
    profileImages: profileImagesProperties,
    followerCount: {
      dataType: 'number',
      name: 'Follower count',
      disabled: true,
    },
    followingCount: {
      dataType: 'number',
      name: 'Following count',
      disabled: true,
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'uid',
    'fullName',
    'displayName',
    'gender',
    'location',
    'bio',
    'photoURL',
    'coverPhotoURL',
    'profileImages',
    'followerCount',
    'followingCount',
    'createdAt',
    'updatedAt',
  ],
});

export const organizationsCollection = buildCollection<Organization>({
  id: 'organizations',
  path: 'organizations',
  name: 'Organizations',
  singularName: 'Organization',
  customId: false,
  group: 'Core',
  permissions: adminOnlyPermissions,
  subcollections: [membersCollection, itemsCollection],
  properties: {
    name: {
      dataType: 'string',
      name: 'Name',
      validation: {
        required: true,
        max: 120,
        trim: true,
      },
    },
    ownerUid: {
      dataType: 'string',
      name: 'Owner UID',
      userSelect: true,
      validation: { required: true },
    },
    ...adminManagedDates,
  },
  propertiesOrder: ['name', 'ownerUid', 'createdAt', 'updatedAt'],
});

const commentReactionsCollection = buildCollection<CommentReactionRecord>({
  id: 'post_comment_reactions',
  path: 'reactions',
  name: 'Reactions',
  singularName: 'Reaction',
  description: 'One active reaction per signed-in user on a comment.',
  customId: true,
  group: 'Posts',
  permissions: adminOnlyPermissions,
  properties: {
    uid: {
      dataType: 'string',
      name: 'UID',
      userSelect: true,
      validation: { required: true },
    },
    postId: {
      dataType: 'string',
      name: 'Post ID',
      validation: { required: true },
    },
    commentId: {
      dataType: 'string',
      name: 'Comment ID',
      validation: { required: true },
    },
    reaction: {
      dataType: 'string',
      name: 'Reaction',
      enumValues: {
        like: 'Like',
        love: 'Love',
        laugh: 'Laugh',
        wow: 'Wow',
        fire: 'Fire',
      },
      validation: { required: true },
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'uid',
    'postId',
    'commentId',
    'reaction',
    'createdAt',
    'updatedAt',
  ],
});

const commentsCollection = buildCollection<PostComment>({
  id: 'post_comments',
  path: 'comments',
  name: 'Comments',
  singularName: 'Comment',
  description:
    'Post comments with optional image, video, or GIPHY media. Use moderation judgment before editing.',
  customId: true,
  group: 'Posts',
  subcollections: [commentReactionsCollection],
  permissions: adminOnlyPermissions,
  properties: {
    authorUid: {
      dataType: 'string',
      name: 'Author UID',
      userSelect: true,
      validation: { required: true },
    },
    authorFullName: {
      dataType: 'string',
      name: 'Author full name',
      clearable: true,
      validation: { max: 80, trim: true },
    },
    authorDisplayName: {
      dataType: 'string',
      name: 'Legacy author display name',
      clearable: true,
      validation: { max: 80, trim: true },
      disabled: true,
    },
    authorPhotoURL: {
      dataType: 'string',
      name: 'Author photo URL',
      url: 'image',
      clearable: true,
      validation: { max: 2048 },
    },
    body: {
      dataType: 'string',
      name: 'Body',
      multiline: true,
      clearable: true,
      validation: { max: 1000 },
    },
    media: {
      dataType: 'map',
      name: 'Media',
      properties: {
        kind: {
          dataType: 'string',
          name: 'Kind',
          enumValues: {
            image: 'Image',
            video: 'Video',
            gif: 'GIPHY GIF',
          },
        },
        source: {
          dataType: 'string',
          name: 'Source',
          enumValues: {
            upload: 'Firebase Storage upload',
            giphy: 'GIPHY',
          },
        },
        storagePath: {
          dataType: 'string',
          name: 'Storage path',
        },
        downloadURL: {
          dataType: 'string',
          name: 'Download URL',
          url: true,
        },
        contentType: {
          dataType: 'string',
          name: 'Content type',
        },
        sizeBytes: {
          dataType: 'number',
          name: 'Size bytes',
        },
        width: {
          dataType: 'number',
          name: 'Width',
        },
        height: {
          dataType: 'number',
          name: 'Height',
        },
        durationMillis: {
          dataType: 'number',
          name: 'Duration milliseconds',
        },
        ...thumbnailProperties,
        giphyId: {
          dataType: 'string',
          name: 'GIPHY ID',
        },
        title: {
          dataType: 'string',
          name: 'GIPHY title',
        },
        pageURL: {
          dataType: 'string',
          name: 'GIPHY page URL',
          url: true,
        },
        previewURL: {
          dataType: 'string',
          name: 'GIPHY preview URL',
          url: true,
        },
        originalURL: {
          dataType: 'string',
          name: 'GIPHY original URL',
          url: true,
        },
      },
    },
    reactionCounts: commentReactionCountsProperties,
    reactionCount: {
      dataType: 'number',
      name: 'Total reactions',
      disabled: true,
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'authorUid',
    'authorFullName',
    'authorDisplayName',
    'authorPhotoURL',
    'body',
    'media',
    'reactionCounts',
    'reactionCount',
    'createdAt',
    'updatedAt',
  ],
});

const postReactionsCollection = buildCollection<PostReactionRecord>({
  id: 'post_reactions',
  path: 'reactions',
  name: 'Post reactions',
  singularName: 'Post reaction',
  description: 'One active reaction per signed-in user on a post.',
  customId: true,
  group: 'Posts',
  permissions: adminOnlyPermissions,
  properties: {
    uid: {
      dataType: 'string',
      name: 'UID',
      userSelect: true,
      validation: { required: true },
    },
    postId: {
      dataType: 'string',
      name: 'Post ID',
      validation: { required: true },
    },
    reaction: {
      dataType: 'string',
      name: 'Reaction',
      enumValues: {
        like: 'Like',
        love: 'Love',
        laugh: 'Laugh',
        wow: 'Wow',
        fire: 'Fire',
      },
      validation: { required: true },
    },
    ...adminManagedDates,
  },
  propertiesOrder: ['uid', 'postId', 'reaction', 'createdAt', 'updatedAt'],
});

export const postsCollection = buildCollection<Post>({
  id: 'posts',
  path: 'posts',
  name: 'Posts',
  singularName: 'Post',
  description:
    'Frienzzi posts with Public, Friends, or Just Me visibility. Authenticated users create posts; admins can manage them here.',
  customId: false,
  group: 'Core',
  permissions: adminOnlyPermissions,
  subcollections: [postReactionsCollection, commentsCollection],
  properties: {
    authorUid: {
      dataType: 'string',
      name: 'Author UID',
      userSelect: true,
      validation: { required: true },
    },
    authorFullName: {
      dataType: 'string',
      name: 'Author full name',
      clearable: true,
      validation: {
        max: 80,
        trim: true,
      },
    },
    authorDisplayName: {
      dataType: 'string',
      name: 'Legacy author display name',
      clearable: true,
      disabled: true,
      validation: {
        max: 80,
        trim: true,
      },
    },
    body: {
      dataType: 'string',
      name: 'Body',
      multiline: true,
      clearable: true,
      validation: {
        max: 2000,
      },
    },
    visibility: {
      dataType: 'string',
      name: 'Visibility',
      enumValues: {
        friends: 'Friends',
        just_me: 'Just me',
        public: 'Public',
      },
      validation: { required: true },
    },
    media: {
      dataType: 'map',
      name: 'Media',
      properties: {
        kind: {
          dataType: 'string',
          name: 'Kind',
          enumValues: {
            image: 'Image',
            video: 'Video',
          },
        },
        storagePath: {
          dataType: 'string',
          name: 'Storage path',
        },
        downloadURL: {
          dataType: 'string',
          name: 'Download URL',
          url: true,
        },
        contentType: {
          dataType: 'string',
          name: 'Content type',
        },
        sizeBytes: {
          dataType: 'number',
          name: 'Size bytes',
        },
        width: {
          dataType: 'number',
          name: 'Width',
        },
        height: {
          dataType: 'number',
          name: 'Height',
        },
        durationMillis: {
          dataType: 'number',
          name: 'Duration milliseconds',
        },
        ...thumbnailProperties,
      },
    },
    likeCount: {
      dataType: 'number',
      name: 'Legacy like count',
      disabled: true,
    },
    reactionCounts: commentReactionCountsProperties,
    reactionCount: {
      dataType: 'number',
      name: 'Total reactions',
      disabled: true,
    },
    commentCount: {
      dataType: 'number',
      name: 'Comment count',
      disabled: true,
    },
    shareCount: {
      dataType: 'number',
      name: 'Share count',
      disabled: true,
    },
    commentMutation: {
      dataType: 'map',
      name: 'Latest comment mutation',
      disabled: true,
      properties: {
        commentId: {
          dataType: 'string',
          name: 'Comment ID',
        },
        actorUid: {
          dataType: 'string',
          name: 'Actor UID',
        },
        operation: {
          dataType: 'string',
          name: 'Operation',
          enumValues: {
            create: 'Create',
            delete: 'Delete',
          },
        },
        updatedAt: {
          dataType: 'date',
          name: 'Updated at',
        },
      },
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'authorUid',
    'authorFullName',
    'authorDisplayName',
    'body',
    'visibility',
    'media',
    'likeCount',
    'reactionCounts',
    'reactionCount',
    'commentCount',
    'shareCount',
    'commentMutation',
    'createdAt',
    'updatedAt',
  ],
});

export const emailNotificationEventsCollection =
  buildCollection<EmailNotificationEvent>({
    id: 'email_notification_events',
    path: 'emailNotificationEvents',
    name: 'Email notification events',
    singularName: 'Email notification event',
    description: 'Admin-only queue for SendGrid notification email delivery.',
    customId: true,
    group: 'Core',
    permissions: adminOnlyPermissions,
    properties: {
      recipientUid: {
        dataType: 'string',
        name: 'Recipient UID',
        validation: { required: true },
      },
      actorUid: {
        dataType: 'string',
        name: 'Actor UID',
        validation: { required: true },
      },
      actorFullName: {
        dataType: 'string',
        name: 'Actor full name',
        clearable: true,
      },
      actorPhotoURL: {
        dataType: 'string',
        name: 'Actor photo URL',
        clearable: true,
      },
      type: {
        dataType: 'string',
        name: 'Type',
      enumValues: {
        post_like: 'Post like',
        post_reaction: 'Post reaction',
        post_comment: 'Post comment',
        profile_follow: 'Profile follow',
        comment_reaction: 'Comment reaction',
        platform_update_digest: 'Platform update digest',
        },
      },
      delivery: {
        dataType: 'string',
        name: 'Delivery',
        enumValues: {
          immediate: 'Immediate',
          digest: 'Digest',
        },
      },
      status: {
        dataType: 'string',
        name: 'Status',
        enumValues: {
          queued: 'Queued',
          sending: 'Sending',
          sent: 'Sent',
          failed: 'Failed',
          skipped: 'Skipped',
        },
      },
      postId: {
        dataType: 'string',
        name: 'Post ID',
        clearable: true,
      },
      commentId: {
        dataType: 'string',
        name: 'Comment ID',
        clearable: true,
      },
      reason: {
        dataType: 'string',
        name: 'Reason',
        enumValues: {
          post_owner: 'Post owner',
          comment_thread: 'Comment thread',
        },
        clearable: true,
      },
      postPreview: {
        dataType: 'string',
        name: 'Post preview',
        clearable: true,
      },
      commentPreview: {
        dataType: 'string',
        name: 'Comment preview',
        clearable: true,
      },
      error: {
        dataType: 'string',
        name: 'Error',
        clearable: true,
        disabled: true,
      },
      createdAt: {
        dataType: 'date',
        name: 'Created at',
        disabled: true,
      },
      updatedAt: {
        dataType: 'date',
        name: 'Updated at',
        disabled: true,
      },
      sentAt: {
        dataType: 'date',
        name: 'Sent at',
        disabled: true,
      },
      failedAt: {
        dataType: 'date',
        name: 'Failed at',
        disabled: true,
      },
      skippedAt: {
        dataType: 'date',
        name: 'Skipped at',
        disabled: true,
      },
    },
    propertiesOrder: [
      'recipientUid',
      'actorUid',
      'actorFullName',
      'actorPhotoURL',
      'type',
      'delivery',
      'status',
      'postId',
      'commentId',
      'reason',
      'postPreview',
      'commentPreview',
      'error',
      'createdAt',
      'updatedAt',
      'sentAt',
      'failedAt',
      'skippedAt',
    ],
  });

export const pushNotificationEventsCollection =
  buildCollection<PushNotificationEvent>({
    id: 'push_notification_events',
    path: 'pushNotificationEvents',
    name: 'Push notification events',
    singularName: 'Push notification event',
    description: 'Admin-only queue for Firebase Cloud Messaging web push.',
    customId: true,
    group: 'Core',
    permissions: adminOnlyPermissions,
    properties: {
      ...emailNotificationEventsCollection.properties,
      notificationId: {
        dataType: 'string',
        name: 'Notification ID',
      },
      tokenCount: {
        dataType: 'number',
        name: 'Token count',
        disabled: true,
      },
      successCount: {
        dataType: 'number',
        name: 'Success count',
        disabled: true,
      },
      failureCount: {
        dataType: 'number',
        name: 'Failure count',
        disabled: true,
      },
      sendingAt: {
        dataType: 'date',
        name: 'Sending at',
        disabled: true,
      },
    },
    propertiesOrder: [
      'recipientUid',
      'actorUid',
      'actorFullName',
      'actorPhotoURL',
      'type',
      'status',
      'notificationId',
      'postId',
      'commentId',
      'reason',
      'postPreview',
      'commentPreview',
      'tokenCount',
      'successCount',
      'failureCount',
      'error',
      'createdAt',
      'updatedAt',
      'sendingAt',
      'sentAt',
      'failedAt',
      'skippedAt',
    ],
  });

export const changeLogEntriesCollection = buildCollection<ChangeLogEntry>({
  id: 'change_log_entries',
  path: 'changeLogEntries',
  name: 'Public changelog',
  singularName: 'Changelog entry',
  description:
    'Plain-language Frienzzi changes for user-visible functionality, privacy, safety, notifications, email behavior, and algorithms.',
  customId: true,
  group: 'Core',
  permissions: adminOnlyPermissions,
  properties: {
    title: {
      dataType: 'string',
      name: 'Title',
      validation: { required: true, max: 120, trim: true },
    },
    summary: {
      dataType: 'string',
      name: 'Summary',
      multiline: true,
      validation: { required: true, max: 500, trim: true },
    },
    details: {
      dataType: 'string',
      name: 'Details',
      multiline: true,
      validation: { max: 4000, trim: true },
    },
    category: {
      dataType: 'string',
      name: 'Category',
      enumValues: {
        functionality: 'Functionality',
        algorithm: 'Algorithm',
        privacy: 'Privacy',
        safety: 'Safety',
        notifications: 'Notifications',
        email: 'Email',
        bugfix: 'Bug fix',
        other: 'Other',
      },
      validation: { required: true },
    },
    impact: {
      dataType: 'string',
      name: 'Impact',
      enumValues: {
        major: 'Major',
        minor: 'Minor',
      },
      validation: { required: true },
    },
    status: {
      dataType: 'string',
      name: 'Status',
      enumValues: {
        draft: 'Draft',
        published: 'Published',
      },
      validation: { required: true },
    },
    visibility: {
      dataType: 'string',
      name: 'Visibility',
      enumValues: {
        public: 'Public',
        internal: 'Internal',
      },
      validation: { required: true },
    },
    effectiveAt: {
      dataType: 'date',
      name: 'Effective at',
      validation: { required: true },
    },
    publishedAt: {
      dataType: 'date',
      name: 'Published at',
      clearable: true,
    },
    commitSha: {
      dataType: 'string',
      name: 'Commit SHA',
      clearable: true,
    },
    releaseId: {
      dataType: 'string',
      name: 'Release ID',
      clearable: true,
    },
    deployTarget: {
      dataType: 'string',
      name: 'Deploy target',
      clearable: true,
    },
    authorName: {
      dataType: 'string',
      name: 'Author name',
      clearable: true,
    },
    ...adminManagedDates,
  },
  propertiesOrder: [
    'title',
    'summary',
    'details',
    'category',
    'impact',
    'status',
    'visibility',
    'effectiveAt',
    'publishedAt',
    'commitSha',
    'releaseId',
    'deployTarget',
    'authorName',
    'createdAt',
    'updatedAt',
  ],
});

export const legalDocumentsCollection = buildCollection<LegalDocumentRecord>({
  id: 'legal_documents',
  path: 'legalDocuments',
  name: 'Legal documents',
  singularName: 'Legal document',
  description:
    'Canonical Terms of Service and Privacy Policy versions seeded from the app source.',
  customId: true,
  group: 'Legal',
  permissions: () => ({
    read: true,
    edit: false,
    create: false,
    delete: false,
  }),
  properties: {
    id: {
      dataType: 'string',
      name: 'Document ID',
      disabled: true,
      validation: { required: true },
    },
    kind: {
      dataType: 'string',
      name: 'Kind',
      enumValues: {
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
      },
      disabled: true,
      validation: { required: true },
    },
    title: {
      dataType: 'string',
      name: 'Title',
      disabled: true,
      validation: { required: true },
    },
    version: {
      dataType: 'string',
      name: 'Version',
      disabled: true,
      validation: { required: true },
    },
    effectiveDate: {
      dataType: 'string',
      name: 'Effective date',
      disabled: true,
      validation: { required: true },
    },
    contentHash: {
      dataType: 'string',
      name: 'Content hash',
      disabled: true,
      validation: { required: true },
    },
    status: {
      dataType: 'string',
      name: 'Status',
      enumValues: {
        current: 'Current',
      },
      disabled: true,
      validation: { required: true },
    },
    sections: {
      dataType: 'array',
      name: 'Sections',
      disabled: true,
      of: {
        dataType: 'map',
        properties: {
          title: {
            dataType: 'string',
            name: 'Title',
          },
          body: {
            dataType: 'array',
            name: 'Body',
            of: {
              dataType: 'string',
            },
          },
        },
      },
    },
    seededAt: {
      dataType: 'date',
      name: 'Seeded at',
      disabled: true,
    },
    updatedAt: {
      dataType: 'date',
      name: 'Updated at',
      disabled: true,
    },
  },
  propertiesOrder: [
    'id',
    'kind',
    'title',
    'version',
    'effectiveDate',
    'contentHash',
    'status',
    'sections',
    'seededAt',
    'updatedAt',
  ],
});

export const coreCollections = [
  usersCollection,
  publicProfilesCollection,
  organizationsCollection,
  postsCollection,
  changeLogEntriesCollection,
  emailNotificationEventsCollection,
  pushNotificationEventsCollection,
  legalDocumentsCollection,
];
